import { defineStore } from 'pinia';
import axios from 'axios';
import { useAuthStore } from './auth';
import { paymentRequest } from '@/services/supplierPayments';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL ?? 'http://localhost:1337';
const DEFAULT_PAGE_SIZE = 100;
const SUPPLIER_STATUSES = new Set(['in_progress', 'unpaid', 'paid']);

const applyNotDeletedFilter = (params = {}) => {
	params['filters[$or][0][isDeleted][$eq]'] = false;
	params['filters[$or][1][isDeleted][$null]'] = true;
	return params;
};

const normalizeRelation = (payload) => {
	if (!payload) return null;
	const data = payload.data || payload;
	if (!data) return null;
	const attributes = data.attributes || {};
	return {
		id: data.id ?? attributes.id ?? null,
		name: attributes.name ?? data.name ?? '',
	};
};

const normalizeFile = (payload) => {
	if (!payload) return null;
	const data = payload.data || payload;
	if (!data) return null;
	const attributes = data.attributes || {};
	return {
		id: data.id ?? attributes.id ?? null,
		name: attributes.name ?? data.name ?? '',
		url: attributes.url ?? data.url ?? '',
		mime: attributes.mime ?? data.mime ?? '',
		ext: attributes.ext ?? data.ext ?? '',
	};
};

const normalizeInvoice = (entry) => {
	const attributes = entry?.attributes || entry || {};
	return {
		id: entry?.id ?? attributes.id ?? null,
		documentId: attributes.documentId ?? entry?.documentId ?? null,
		status: attributes.status ?? 'unpaid',
		paidDate: attributes.paidDate ?? null,
		invoiceNumber: attributes.invoiceNumber ?? '',
		invoiceDate: attributes.invoiceDate ?? null,
		isVerified: attributes.isVerified ?? false,
		isDeleted: Boolean(attributes.isDeleted ?? false),
		deletedAt: attributes.deletedAt ?? null,
		totalAmount: attributes.totalAmount ?? null,
		purchaseExtraction: attributes.purchaseExtraction ?? null,
		receivedAt: attributes.receivedAt ?? null,
		servicePeriodStart: attributes.servicePeriodStart ?? null,
		servicePeriodEnd: attributes.servicePeriodEnd ?? null,
		sourceEmail: attributes.sourceEmail ?? '',
		sourceSubject: attributes.sourceSubject ?? '',
		supplierNameRaw: attributes.supplierNameRaw ?? '',
		matchConfidence: attributes.matchConfidence ?? null,
		attachmentName: attributes.attachmentName ?? '',
		archiveKey: attributes.archiveKey ?? '',
		supplier: normalizeRelation(attributes.supplier),
		file: normalizeFile(attributes.file),
	};
};

const resolveInvoiceKey = (item) => {
	const key = item?.documentId ?? item?.id;
	if (key === null || key === undefined || key === '') return '';
	return String(key);
};

const mergeInvoiceLists = (preferred = [], fallback = []) => {
	const merged = [];
	const seenKeys = new Set();

	const pushItem = (item) => {
		const key = resolveInvoiceKey(item);
		if (!key) {
			merged.push(item);
			return;
		}
		if (seenKeys.has(key)) return;
		seenKeys.add(key);
		merged.push(item);
	};

	preferred.forEach(pushItem);
	fallback.forEach(pushItem);
	return merged;
};

const toSortTimestamp = (value) => {
	if (!value) return 0;
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const compareInvoiceMatchPriority = (left, right) => {
	const leftVerified = left?.isVerified ? 1 : 0;
	const rightVerified = right?.isVerified ? 1 : 0;
	if (leftVerified !== rightVerified) {
		return rightVerified - leftVerified;
	}

	const leftTime = toSortTimestamp(left?.receivedAt || left?.invoiceDate);
	const rightTime = toSortTimestamp(right?.receivedAt || right?.invoiceDate);
	return rightTime - leftTime;
};

const mergeSavedInvoiceIntoItems = (items = [], saved = null, { replaceSupplier = false } = {}) => {
	const savedKey = saved?.documentId ?? saved?.id;
	if (!savedKey) return items;
	return items.map((item) => {
		const key = item?.documentId ?? item?.id;
		if (key && String(key) === String(savedKey)) {
			return {
				...item,
				...saved,
				supplier: replaceSupplier ? saved.supplier : saved.supplier || item.supplier,
				file: saved.file || item.file,
			};
		}
		return item;
	});
};

export const useSupplierInvoicesStore = defineStore('supplierInvoices', {
	state: () => ({
		items: [],
		// Invoices the bot is still reading (see fetchProcessingInvoices).
		processingItems: [],
		isLoading: false,
		isRefreshing: false,
		error: null,
		hasFetched: false,
		pagination: {
			page: 1,
			pageSize: DEFAULT_PAGE_SIZE,
			total: 0,
			pageCount: 1,
		},
		filters: {
			status: '',
			supplierId: '',
			dateFrom: '',
			dateTo: '',
		},
	}),
	getters: {
		invoiceList: (state) => state.items,
	},
	actions: {
		authHeaders() {
			const authStore = useAuthStore();
			return authStore.token ? authStore.requestHeaders.headers : {};
		},

		basePopulateParams() {
			return {
				'populate[supplier][fields][0]': 'name',
				'populate[file][fields][0]': 'name',
				'populate[file][fields][1]': 'url',
				'populate[file][fields][2]': 'mime',
				'populate[file][fields][3]': 'ext',
			};
		},

		normalizeDateRange(from, to) {
			const parse = (value) => {
				if (!value) return null;
				const date = new Date(value);
				return Number.isNaN(date.getTime()) ? null : { date, raw: value };
			};

			let start = parse(from);
			let end = parse(to);

			if (start && end && start.date > end.date) {
				[start, end] = [end, start];
			}
			if (start && !end) {
				end = start;
			} else if (!start && end) {
				start = end;
			}

			return {
				from: start?.raw || '',
				to: end?.raw || '',
			};
		},

		hasActiveFilters() {
			const { from, to } = this.normalizeDateRange(this.filters.dateFrom, this.filters.dateTo);
			const normalizedStatus = String(this.filters.status || '').trim().toLowerCase();
			return Boolean(
				this.filters.supplierId ||
				(normalizedStatus && normalizedStatus !== 'all') ||
				from ||
				to
			);
		},

		buildQueryParams(overrides = {}) {
			const page = overrides.page ?? this.pagination.page;
			const pageSize = overrides.pageSize ?? this.pagination.pageSize;
			const params = applyNotDeletedFilter({
				'pagination[page]': page,
				'pagination[pageSize]': pageSize,
				'sort[0]': 'invoiceDate:desc',
				'sort[1]': 'receivedAt:desc',
				'sort[2]': 'id:desc',
				...this.basePopulateParams(),
			});

			const normalizedStatus = String(this.filters.status || '').trim().toLowerCase();
			if (normalizedStatus === 'unpaid') {
				params['filters[status][$notIn][0]'] = 'paid';
				params['filters[status][$notIn][1]'] = 'in_progress';
			} else if (SUPPLIER_STATUSES.has(normalizedStatus)) {
				params['filters[status][$eq]'] = normalizedStatus;
			}

			if (this.filters.supplierId) {
				params['filters[supplier][id][$eq]'] = this.filters.supplierId;
			}

			const { from: normalizedFrom, to: normalizedTo } = this.normalizeDateRange(
				this.filters.dateFrom,
				this.filters.dateTo,
			);
			if (normalizedFrom) {
				params['filters[invoiceDate][$gte]'] = normalizedFrom;
			}
			if (normalizedTo) {
				params['filters[invoiceDate][$lte]'] = normalizedTo;
			}

			return params;
		},

		async findInvoiceMatchesByExactPeriod({ supplierId, startDate, endDate } = {}) {
			if (!supplierId || !startDate || !endDate) return [];
			try {
				const headers = this.authHeaders();
				const params = {
					'pagination[page]': 1,
					'pagination[pageSize]': 100,
					...this.basePopulateParams(),
					'filters[supplier][id][$eq]': supplierId,
					'filters[$or][0][isDeleted][$eq]': false,
					'filters[$or][1][isDeleted][$null]': true,
					'filters[status][$ne]': 'in_progress',
					'filters[file][id][$notNull]': true,
					'filters[servicePeriodStart][$eq]': startDate,
					'filters[servicePeriodEnd][$eq]': endDate,
				};

				const response = await axios.get(`${STRAPI_URL}/api/supplier-invoices`, {
					headers,
					params,
				});
				const entries = response?.data?.data || [];
				return entries
					.map(normalizeInvoice)
					.filter((invoice) => !invoice?.isDeleted)
					.sort(compareInvoiceMatchPriority);
			} catch (error) {
				if (import.meta.env.DEV) {
					// eslint-disable-next-line no-console
					console.warn('[supplierInvoices] findInvoiceMatchesByExactPeriod failed', error);
				}
				return [];
			}
		},

		async findInvoiceMatch({ supplierId, startDate, endDate } = {}) {
			const matches = await this.findInvoiceMatchesByExactPeriod({
				supplierId,
				startDate,
				endDate,
			});
			return matches[0] || null;
		},

		async fetchInvoiceByIdOrDocumentId(idOrDocumentId) {
			if (!idOrDocumentId) return null;
			try {
				const response = await axios.get(
					`${STRAPI_URL}/api/supplier-invoices/${idOrDocumentId}`,
					{
						headers: this.authHeaders(),
						params: this.basePopulateParams(),
					},
				);
				const entry = response?.data?.data;
				const normalized = entry ? normalizeInvoice(entry) : null;
				if (normalized?.isDeleted) {
					return null;
				}
				return normalized;
			} catch (error) {
				if (import.meta.env.DEV) {
					// eslint-disable-next-line no-console
					console.warn('[supplierInvoices] fetchInvoiceByIdOrDocumentId failed', error);
				}
				return null;
			}
		},

		async fetchInvoices(options = {}) {
			this._activeFetchToken ??= null;
			const requestToken = Symbol('fetchSupplierInvoices');
			this._activeFetchToken = requestToken;
			this.isLoading = true;
			this.error = null;

			try {
				const shouldFetchAllPages = Boolean(options?.allPages);
				const pageSize =
					options.pageSize ??
					(shouldFetchAllPages ? DEFAULT_PAGE_SIZE : this.pagination.pageSize);

				const fetchPage = async (page) => {
					const params = this.buildQueryParams({ ...options, page, pageSize });
					const response = await axios.get(`${STRAPI_URL}/api/supplier-invoices`, {
						headers: this.authHeaders(),
						params,
					});
					const entries = response?.data?.data || [];
					const metaPagination = response?.data?.meta?.pagination || {};
					return { entries, metaPagination };
				};

				const firstPage = shouldFetchAllPages ? 1 : (options.page ?? this.pagination.page ?? 1);
				const { entries: firstEntries, metaPagination } = await fetchPage(firstPage);
				if (this._activeFetchToken !== requestToken) {
					return;
				}
				let allEntries = [...firstEntries];

				const pageCount =
					Number.isFinite(metaPagination.pageCount) && metaPagination.pageCount > 0
						? metaPagination.pageCount
						: 1;

				if (shouldFetchAllPages && pageCount > 1) {
					for (let nextPage = 2; nextPage <= pageCount; nextPage += 1) {
						if (this._activeFetchToken !== requestToken) {
							return;
						}
						// eslint-disable-next-line no-await-in-loop
						const { entries: nextEntries } = await fetchPage(nextPage);
						if (this._activeFetchToken !== requestToken) {
							return;
						}
						allEntries = allEntries.concat(nextEntries);
					}
				}

				this.items = allEntries.map(normalizeInvoice);
				const total = shouldFetchAllPages
					? allEntries.length
					: (metaPagination.total ?? this.pagination.total);

				const nextPageSize = shouldFetchAllPages
					? (pageSize || DEFAULT_PAGE_SIZE)
					: (metaPagination.pageSize ?? pageSize ?? this.pagination.pageSize);

				const nextPage = shouldFetchAllPages
					? 1
					: (metaPagination.page ?? this.pagination.page);

				const nextPageCount = shouldFetchAllPages
					? 1
					: (metaPagination.pageCount ?? this.pagination.pageCount);

				this.pagination = {
					page: nextPage,
					pageSize: nextPageSize,
					total,
					pageCount: nextPageCount,
				};
				this.hasFetched = true;
			} catch (error) {
				if (this._activeFetchToken === requestToken) {
					this.error =
						error?.response?.data?.error?.message ||
						error.message ||
						'Failed to load supplier invoices';
				}
				throw error;
			} finally {
				if (this._activeFetchToken === requestToken) {
					this.isLoading = false;
					this._activeFetchToken = null;
				}
			}
		},

		async fetchNewInvoices() {
			if (this.isRefreshing || this.isLoading || this._activeFetchToken) return 0;
			this.isRefreshing = true;
			const refreshToken = Symbol('refreshSupplierInvoices');
			this._activeRefreshToken = refreshToken;

			try {
				const shouldFetchAllPages = this.hasActiveFilters();
				const pageSize = shouldFetchAllPages
					? DEFAULT_PAGE_SIZE
					: (this.pagination.pageSize || DEFAULT_PAGE_SIZE);
				const fetchPage = async (page) => {
					const response = await axios.get(`${STRAPI_URL}/api/supplier-invoices`, {
						headers: this.authHeaders(),
						params: this.buildQueryParams({ page, pageSize }),
					});
					return {
						entries: response?.data?.data || [],
						metaPagination: response?.data?.meta?.pagination || {},
					};
				};

				const previousKeys = new Set(
					this.items.map((item) => resolveInvoiceKey(item)).filter(Boolean),
				);
				const { entries: firstEntries, metaPagination } = await fetchPage(1);
				if (this._activeRefreshToken !== refreshToken || this._activeFetchToken) {
					return 0;
				}

				if (shouldFetchAllPages) {
					let allEntries = [...firstEntries];
					const pageCount =
						Number.isFinite(metaPagination.pageCount) && metaPagination.pageCount > 0
							? metaPagination.pageCount
							: 1;

					for (let nextPage = 2; nextPage <= pageCount; nextPage += 1) {
						if (this._activeRefreshToken !== refreshToken || this._activeFetchToken) {
							return 0;
						}
						// eslint-disable-next-line no-await-in-loop
						const { entries: nextEntries } = await fetchPage(nextPage);
						if (this._activeRefreshToken !== refreshToken || this._activeFetchToken) {
							return 0;
						}
						allEntries = allEntries.concat(nextEntries);
					}

					const normalizedAllEntries = allEntries.map(normalizeInvoice);
					const addedCount = normalizedAllEntries.reduce((count, item) => {
						const key = resolveInvoiceKey(item);
						return key && !previousKeys.has(key) ? count + 1 : count;
					}, 0);

					this.items = normalizedAllEntries;
					this.pagination = {
						page: 1,
						pageSize,
						total: normalizedAllEntries.length,
						pageCount: 1,
					};
					this.hasFetched = true;
					this.error = null;
					return addedCount;
				}

				const normalizedFirstPage = firstEntries.map(normalizeInvoice);
				const addedCount = normalizedFirstPage.reduce((count, item) => {
					const key = resolveInvoiceKey(item);
					return key && !previousKeys.has(key) ? count + 1 : count;
				}, 0);

				const nextPageSize = metaPagination.pageSize ?? pageSize ?? DEFAULT_PAGE_SIZE;
				const nextPageCount = Math.max(metaPagination.pageCount ?? this.pagination.pageCount ?? 1, 1);
				const nextTotal = metaPagination.total ?? this.pagination.total;
				const currentPage = this.pagination.page ?? 1;
				const nextPage = Math.min(Math.max(currentPage, 1), nextPageCount);

				if (nextPage === 1) {
					const visibleLimit = Math.min(nextPageSize, nextTotal || nextPageSize);
					this.items = mergeInvoiceLists(normalizedFirstPage, this.items).slice(0, visibleLimit);
				}

				this.pagination = {
					...this.pagination,
					page: nextPage,
					pageSize: nextPageSize,
					total: nextTotal,
					pageCount: nextPageCount,
				};
				this.hasFetched = true;
				this.error = null;
				return addedCount;
			} catch (error) {
				if (import.meta.env.DEV) {
					// eslint-disable-next-line no-console
					console.warn('[supplierInvoices] background refresh failed', error);
				}
				return 0;
			} finally {
				if (this._activeRefreshToken === refreshToken) {
					this.isRefreshing = false;
					this._activeRefreshToken = null;
				}
			}
		},

		// An invoice the bot is still reading has no invoice date yet, and the database sorts empty
		// dates last, so it never reaches the first page of the list. It is fetched on its own.
		async fetchProcessingInvoices() {
			try {
				const response = await axios.get(`${STRAPI_URL}/api/supplier-invoices`, {
					headers: this.authHeaders(),
					params: applyNotDeletedFilter({
						'filters[status][$eq]': 'in_progress',
						'sort[0]': 'receivedAt:asc',
						'pagination[page]': 1,
						'pagination[pageSize]': 10,
						...this.basePopulateParams(),
					}),
				});
				this.processingItems = (response?.data?.data || []).map(normalizeInvoice);
			} catch (error) {
				// Keep the last known list; the next check tries again.
				if (import.meta.env.DEV) {
					// eslint-disable-next-line no-console
					console.warn('[supplierInvoices] processing refresh failed', error);
				}
			}
			return this.processingItems;
		},

		async updateFilters(partial = {}) {
			this.filters = { ...this.filters, ...partial };
			this.pagination = { ...this.pagination, page: 1 };
			await this.fetchInvoices({
				page: 1,
				pageSize: DEFAULT_PAGE_SIZE,
				allPages: this.hasActiveFilters(),
			});
		},

		async changePage(page) {
			if (page < 1 || page === this.pagination.page || page > this.pagination.pageCount) {
				return;
			}
			this.pagination = { ...this.pagination, page };
			await this.fetchInvoices({ page });
		},

		async refresh() {
			const shouldFetchAllPages = this.hasActiveFilters();
			const targetPage = shouldFetchAllPages ? 1 : this.pagination.page;
			await this.fetchInvoices({
				page: targetPage,
				pageSize: shouldFetchAllPages ? DEFAULT_PAGE_SIZE : this.pagination.pageSize,
				allPages: shouldFetchAllPages,
			});
		},

		async deleteInvoice({ documentId, id } = {}) {
			const targetId = documentId ?? id;
			if (!targetId) return;
			await axios.delete(`${STRAPI_URL}/api/supplier-invoices/${targetId}`, {
				headers: this.authHeaders(),
			});

			const targetKey = String(targetId);
			this.items = this.items.filter((item) => {
				const key = item?.documentId ?? item?.id;
				return key ? String(key) !== targetKey : true;
			});

			const shouldFetchAllPages = this.hasActiveFilters();
			const targetPage = shouldFetchAllPages ? 1 : this.pagination.page;
			await this.fetchInvoices({
				page: targetPage,
				pageSize: shouldFetchAllPages ? DEFAULT_PAGE_SIZE : this.pagination.pageSize,
				allPages: shouldFetchAllPages,
			});
		},

		async saveInvoiceStatus({ documentId, id, status, paidDate }) {
			const targetId = documentId ?? id;
			if (!targetId) return null;

			const payload = {
				status,
				paidDate: paidDate || null,
			};

			// Existing invoice settlements must stay in sync, without opening an editor.
			const { data: settlement } = await paymentRequest(`/invoice/${targetId}`);
			let response;
			if (settlement && settlement.status !== 'cancelled') {
				await paymentRequest(`/${settlement.documentId}/status`, {
					method: 'post',
					data: {
						...payload,
						revision: settlement.revision,
						reason: 'Payment status updated from the subcontractor invoice list.',
					},
				});
				response = await axios.get(`${STRAPI_URL}/api/supplier-invoices/${targetId}`, {
					headers: this.authHeaders(),
					params: this.basePopulateParams(),
				});
			} else {
				response = await axios.put(
					`${STRAPI_URL}/api/supplier-invoices/${targetId}`,
					{ data: payload },
					{ headers: this.authHeaders() },
				);
			}

			const saved = normalizeInvoice(response?.data?.data || {});
			this.items = mergeSavedInvoiceIntoItems(this.items, saved);
			return saved;
		},

		async saveInvoiceMetadata({
			documentId,
			id,
			invoiceNumber = undefined,
			invoiceDate = undefined,
			supplierId = undefined,
			servicePeriodStart = undefined,
			servicePeriodEnd = undefined,
		}) {
			const targetId = documentId ?? id;
			if (!targetId) return null;

			const payload = {};
			if (invoiceNumber !== undefined) {
				payload.invoiceNumber = invoiceNumber || null;
			}
			if (invoiceDate !== undefined) {
				payload.invoiceDate = invoiceDate || null;
			}
			if (supplierId !== undefined) {
				payload.supplier = supplierId || null;
			}
			if (servicePeriodStart !== undefined) {
				payload.servicePeriodStart = servicePeriodStart || null;
			}
			if (servicePeriodEnd !== undefined) {
				payload.servicePeriodEnd = servicePeriodEnd || null;
			}

			const response = await axios.put(
				`${STRAPI_URL}/api/supplier-invoices/${targetId}`,
				{ data: payload },
				{ headers: this.authHeaders(), params: this.basePopulateParams() },
			);

			const saved = normalizeInvoice(response?.data?.data || {});
			this.items = mergeSavedInvoiceIntoItems(this.items, saved, { replaceSupplier: supplierId !== undefined });
			return saved;
		},

		async saveInvoicePeriod({ documentId, id, servicePeriodStart, servicePeriodEnd }) {
			return this.saveInvoiceMetadata({
				documentId,
				id,
				servicePeriodStart: servicePeriodStart || null,
				servicePeriodEnd: servicePeriodEnd || null,
			});
		},

		async saveInvoiceVerification({ documentId, id, isVerified }) {
			const targetId = documentId ?? id;
			if (!targetId) return null;

			const payload = {
				isVerified: Boolean(isVerified),
			};

			const response = await axios.put(
				`${STRAPI_URL}/api/supplier-invoices/${targetId}`,
				{ data: payload },
				{ headers: this.authHeaders() },
			);

			const saved = normalizeInvoice(response?.data?.data || {});
			this.items = mergeSavedInvoiceIntoItems(this.items, saved);
			return saved;
		},
	},
});
